import {
  CopilotAPIRequestTypeToFieldSelectionInfoMap,
  CopilotCheck,
  GTFAFieldSelectionInfo,
  RubricsFieldSelectionInfo,
  IndividualFieldSelectionInfo,
  CopilotAPIRequestTypeToAdditionalMetadataMap,
  RubricsAdditionalMetadata,
  PromptAmbiguityFieldSelectionInfo,
  CopilotRequirement,
  CopilotDataTaxonomyStep,
  CopilotDataField,
  RubricCriterion,
  CopilotResult,
} from '@scale/shared/types/quality';
import { CopilotAPIRequestType, InteractionStepType } from '@scale/shared/types/chat';
import { CheckRunId } from '@scale/shared/types/quality/contributorCopilot';
import { HillclimbTestRow } from './CopilotConfigurationV2/CopilotHillclimbingTable';
import { arrayToCSV } from 'react-scale/corp/components/UI/DataUpload/arrayToCSV';
import _ from 'lodash';

// Type for binary pass/fail classification translated from 1-5 scores used in stats calculation
type BinaryResult = 'pass' | 'fail';

export type RubricsCopilotCheck = Extract<
  CopilotCheck,
  {
    checkType:
      | CopilotAPIRequestType.RubricsCrossCriteria
      | CopilotAPIRequestType.RubricsPerCriteria;
  }
>;

export type PromptAmbiguityCopilotCheck = Extract<
  CopilotCheck,
  {
    checkType: CopilotAPIRequestType.PromptAmbiguity;
  }
>;

export const supportsAdditionalMetadata = (draft: CopilotCheck): draft is RubricsCopilotCheck =>
  draft.checkType === CopilotAPIRequestType.RubricsCrossCriteria ||
  draft.checkType === CopilotAPIRequestType.RubricsPerCriteria ||
  draft.checkType === CopilotAPIRequestType.PromptAmbiguity;

export const isCopilotCheckInvalid = (draft: CopilotCheck | undefined) => {
  if (!draft || !draft.stepTypes || !draft.stepTypes.length || !draft.name) {
    return true;
  }

  const fieldSelectionInfo = draft.fieldSelectionInfo as Record<string, any>;
  const fieldSelectionMap = CopilotAPIRequestTypeToFieldSelectionInfoMap[draft.checkType];

  // check that all the fields that are required are defined
  for (const fieldName of Object.keys(fieldSelectionMap)) {
    if (fieldSelectionMap[fieldName].required) {
      // validStepIds is non empty
      if (
        !fieldSelectionInfo[fieldName]?.validStepIds ||
        fieldSelectionInfo[fieldName]?.validStepIds.length === 0
      ) {
        return true;
      }
      if (!fieldSelectionInfo[fieldName]?.stepType) {
        return true;
      }
      // fieldId is expected but not found
      if (fieldSelectionMap[fieldName].includeFieldId && !fieldSelectionInfo[fieldName]?.fieldId) {
        return true;
      }
    }
  }

  if (supportsAdditionalMetadata(draft)) {
    const additionalMetadataKeys = Object.keys(
      CopilotAPIRequestTypeToAdditionalMetadataMap[draft.checkType],
    ) as (keyof RubricsAdditionalMetadata)[];
    if (additionalMetadataKeys.length > 0 && !draft.additionalMetadata) {
      return true;
    }

    if (draft.additionalMetadata) {
      for (const fieldName of additionalMetadataKeys) {
        if (
          CopilotAPIRequestTypeToAdditionalMetadataMap[draft.checkType][fieldName].required &&
          !draft.additionalMetadata[fieldName]
        ) {
          return true;
        }
      }
    }
  }

  return false;
};

export const getEmptyFieldSelectionInfo = (checkType: CopilotAPIRequestType) => {
  if (!checkType) {
    return {} as unknown as
      | GTFAFieldSelectionInfo
      | RubricsFieldSelectionInfo
      | PromptAmbiguityFieldSelectionInfo;
  }

  const fieldNames = Object.keys(CopilotAPIRequestTypeToFieldSelectionInfoMap[checkType]);

  const fieldSelectionInfo = fieldNames.reduce(
    (acc: Record<string, IndividualFieldSelectionInfo>, fieldName) => {
      acc[fieldName] = {
        fieldId: '',
        stepType:
          CopilotAPIRequestTypeToFieldSelectionInfoMap[checkType][fieldName].stepType ||
          (undefined as unknown as InteractionStepType),
        validStepIds: [],
      };
      return acc;
    },
    {},
  );

  return fieldSelectionInfo as
    | GTFAFieldSelectionInfo
    | RubricsFieldSelectionInfo
    | PromptAmbiguityFieldSelectionInfo;
};

/**
 * Calculate hillclimbing stats (accuracy, precision, recall, confusion matrix)
 * from binary pass/fail classifications.
 *
 * @param groundTruth - Array of expected results ('pass' or 'fail')
 * @param predicted - Array of actual results ('pass' or 'fail')
 */
export const getCopilotHillclimbingStats = ({
  groundTruth,
  predicted,
}: {
  groundTruth: BinaryResult[];
  predicted: BinaryResult[];
}) => {
  let truePositives = 0;
  let falsePositives = 0;
  let falseNegatives = 0;
  let trueNegatives = 0;

  for (let i = 0; i < groundTruth.length; i++) {
    if (groundTruth[i] === 'pass' && predicted[i] === 'pass') {
      truePositives++;
    } else if (groundTruth[i] === 'fail' && predicted[i] === 'fail') {
      trueNegatives++;
    } else if (groundTruth[i] === 'fail' && predicted[i] === 'pass') {
      falsePositives++;
    } else if (groundTruth[i] === 'pass' && predicted[i] === 'fail') {
      falseNegatives++;
    }
  }

  const accuracy =
    groundTruth.length === 0 ? 0 : (truePositives + trueNegatives) / groundTruth.length;
  const precision =
    truePositives + falsePositives === 0 ? 0 : truePositives / (truePositives + falsePositives);
  const recall =
    truePositives + falseNegatives === 0 ? 0 : truePositives / (truePositives + falseNegatives);

  return {
    precision: Math.round(precision * 100),
    recall: Math.round(recall * 100),
    accuracy: Math.round(accuracy * 100),
    truePositives,
    falsePositives,
    falseNegatives,
    trueNegatives,
    total: groundTruth.length,
  };
};

const sortAdditionalMetadata = (
  additionalMetadata?: Record<string, string>,
): Record<string, string> | undefined => {
  if (!additionalMetadata) {
    return undefined;
  }

  return Object.keys(additionalMetadata)
    .sort()
    .reduce<Record<string, string>>((acc, key) => {
      const value = additionalMetadata[key];
      if (typeof value !== 'undefined') {
        acc[key] = value;
      }
      return acc;
    }, {});
};

export const serializeCheckRunId = (checkRun: CheckRunId): string => {
  const normalized: CheckRunId = {
    attemptId: checkRun.attemptId,
    turnId: checkRun.turnId,
    stageId: checkRun.stageId,
    ...(checkRun.additionalMetadata
      ? { additionalMetadata: sortAdditionalMetadata(checkRun.additionalMetadata) }
      : {}),
  };

  return JSON.stringify(normalized);
};

export const getRubricDataFieldFromRequirement = (requirement?: CopilotRequirement) => {
  if (!requirement) {
    return undefined;
  }

  const allDataFields = requirement.selected_steps.flatMap(
    (step: CopilotDataTaxonomyStep) => step.selected_data_fields,
  );

  return allDataFields.find((field: CopilotDataField) => field.field_name === 'criteria');
};

export const formatStepDataForDisplay = (
  stepData: Record<string, any>,
  criterionId?: string,
  effectiveRequirement?: CopilotRequirement,
): [string, any][] | 'Unknown' => {
  if (!stepData) {
    return 'Unknown';
  }

  const content = _.cloneDeep(stepData);
  const rubricFieldName = getRubricDataFieldFromRequirement(effectiveRequirement)?.rename;

  if (criterionId) {
    if (rubricFieldName) {
      const criterion = stepData[rubricFieldName]?.find(
        (criterion: RubricCriterion) => criterion.id === criterionId,
      );
      content[rubricFieldName] = [criterion];
    }
  }

  // make sure prompt is first, then sort alphabetically
  return Object.entries(content).sort((a, b) => {
    const [keyA] = a;
    const [keyB] = b;

    if (keyA === 'prompt') return -1;
    if (keyB === 'prompt') return 1;

    return keyA.localeCompare(keyB);
  });
};

export const extractHillclimbResultsCsvData = (
  hillclimbRows: HillclimbTestRow[],
  allStepData: Record<string, any>[] | undefined,
  effectiveRequirement: CopilotRequirement | undefined,
  getExpectedResult: (rowId: string) => CopilotResult,
  getExpectedReasoning: (rowId: string) => string | undefined,
  getVerified: (rowId: string) => boolean,
) => {
  return arrayToCSV(
    hillclimbRows
      .filter(row => row.status !== 'error')
      .map(row => {
        const { criterionId, turnIdx, attemptId, score, explanation, feedback, id } = row;
        let stepData = formatStepDataForDisplay(
          allStepData?.find(data => data.attemptId === attemptId && data.turnIdx === turnIdx)
            ?.stepData,
          criterionId,
          effectiveRequirement,
        );

        if (stepData === undefined || stepData === 'Unknown') {
          stepData = [];
        }

        const formattedStepData: Record<string, any> = {};
        for (const [dataFieldName, value] of stepData) {
          const jsonString = JSON.stringify(value);
          formattedStepData[dataFieldName] = `"${jsonString?.replace(/"/g, '""')}"`;
        }

        return {
          attempt_id: attemptId ?? '',
          qc_score: getExpectedResult(id) ?? '',
          qc_feedback: `"${(getExpectedReasoning(id) ?? '')?.replace(/"/g, '""')}"`,
          verified: getVerified(id),
          criterion_id: criterionId ?? '/', // format this for non criterion also
          copilot_score: score ?? '',
          copilot_reasoning: `"${(explanation ?? '')?.replace(/"/g, '""')}"`,
          copilot_feedback: `"${(feedback ?? '')?.replace(/"/g, '""')}"`,
          ...formattedStepData,
        };
      }),
  );
};
